import axios from './axios'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useUserStore } from '@/stores/userStore'

export interface ErrorReportPayload {
  userId?: string // Azure AD B2C GUID
  systemUserId?: string // Database user ID
  userEmail?: string
  organizerId?: string
  organizerName?: string
  eventId?: number
  eventTitle?: string
  errorMessage?: string
  errorDetails?: string
  userAdditionalInfo?: string // User-provided additional information
  timestamp?: string
  url?: string
  userAgent?: string
  statusCode?: number
  source?: string // e.g., "step3", "search", "auth", "module-import"
}

export interface ErrorReportResponse {
  success: boolean
  message?: string
  timestamp?: string
  supportEmail?: string
}

async function reportToEndpoint(endpoint: string, payload: ErrorReportPayload): Promise<ErrorReportResponse | null> {
  try {
    const authStore = useAuthenticationStore()
    const userStore = useUserStore()
    
    // DO NOT call userStore.loadUser() - it triggers auth flows
    // Instead, use user data if already loaded (userStore initializes with immediate: true)
    // This gives us systemUserId if the store was already initialized
    let systemUserId: string | null = null
    try {
      // userStore.user should already be loaded from automatic initialization
      // Only access it - don't call any methods that would trigger loading
      if (userStore.user?.id) {
        systemUserId = userStore.user.id
      }
    } catch (err) {
      // userStore might not be initialized yet - that's okay, continue without systemUserId
    }

    // Populate payload with available user data
    // userId = Azure AD B2C GUID (from authStore)
    // systemUserId = Database user ID (from userStore if available)
    // userEmail = user email (from authStore or userStore)
    const fullPayload = {
      ...payload,
      userId: payload.userId || authStore.userId || undefined,
      systemUserId: payload.systemUserId || systemUserId || undefined,
      userEmail: payload.userEmail || authStore.userEmail || userStore.user?.email || undefined,
      timestamp: payload.timestamp || new Date().toISOString(),
      url: payload.url || window.location.href,
      userAgent: payload.userAgent || navigator.userAgent,
    }

    console.log('[reportError] Sending error report to backend:', {
      message: fullPayload.errorMessage,
      source: fullPayload.source,
      timestamp: fullPayload.timestamp
    })

    if (!payload.errorMessage && !payload.errorDetails) {
      console.warn('[reportError] No error to report - message and details both empty')
      return null
    }
    
    let response: any = null
    let axiosSuccess = false
    
    try {
      // Use plain axios (not eventsAuthenticatedAxios) to avoid MSAL token redirect
      // Error reporting endpoint is anonymous - doesn't need authentication
      response = await Promise.race([
        axios.post<ErrorReportResponse>(
          endpoint,
          fullPayload,
          { timeout: 30000 }
        ),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('error report request timeout')), 35000)
        )
      ])
      axiosSuccess = true
      console.log('✅ [reportError] Error submitted successfully')
    } catch (axiosError) {
      console.error('❌ [reportError] Failed to submit error report:', 
        (axiosError as any)?.message || axiosError)
      // Don't throw - this is a secondary operation
    }

    // Return response or null if failed
    if (axiosSuccess && response?.data?.success) {
      return response.data
    }
    return null
  } catch (error) {
    console.error('❌ [reportError] Unexpected error:', error)
    // Don't throw - allow error reporting to fail silently
    return null
  }
}

export async function reportError(payload: ErrorReportPayload): Promise<ErrorReportResponse | null> {
  return reportToEndpoint('/errors/report', payload)
}

export async function reportSupport(payload: ErrorReportPayload): Promise<ErrorReportResponse | null> {
  return reportToEndpoint('/support/report', payload)
}

/**
 * Reports error with details in JSON format
 * Useful for structured error reporting from specific components
 */
export async function reportErrorWithDetails(
  message: string,
  details: string,
  context?: {
    eventId?: number
    eventTitle?: string
    organizerId?: string
    organizerName?: string
    source?: string
    statusCode?: number
    userAdditionalInfo?: string
  }
): Promise<ErrorReportResponse | null> {
  return reportError({
    errorMessage: message,
    errorDetails: details,
    eventId: context?.eventId,
    eventTitle: context?.eventTitle,
    organizerId: context?.organizerId,
    organizerName: context?.organizerName,
    source: context?.source,
    statusCode: context?.statusCode,
    userAdditionalInfo: context?.userAdditionalInfo,
  })
}
